import { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  MdClose,
  MdPhotoCamera,
  MdCheckCircle,
  MdDeleteOutline,
} from "react-icons/md";
import {
  OBSERVATION_REASON_MAP,
  OBSERVATION_REASON_OPTIONS,
} from "../../../helpers/incomeEntry";
import ConfirmDialog from "components/utils/ConfirmDialog";
import { typographyStyles } from "constants/styles";
import type { IIncomeEntryArticleBundleObservation } from "types";
import { FILE_IMAGE_CONTENT_TYPES } from "constants/index";
import useFileUpload from "hooks/useFileUpload";

function canSaveObservation(obs: Partial<IIncomeEntryArticleBundleObservation> | undefined): boolean {
  return obs?.reason !== undefined;
}

interface IncomeEntryArticleBundleObservationCardProps {
  bundleId: number;
  observation?: Partial<IIncomeEntryArticleBundleObservation>;
  open: boolean;
  onSave: (bundleId: number, obs: Omit<IIncomeEntryArticleBundleObservation, "id">) => void;
  onRemove: () => void;
  onClose: () => void;
}

export default function IncomeEntryArticleBundleObservationCard({
  bundleId,
  observation,
  open,
  onSave,
  onRemove,
  onClose,
}: IncomeEntryArticleBundleObservationCardProps) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [photoName, setPhotoName] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const uploadAttemptRef = useRef<symbol | null>(null);
  const { upload, reset, status, progress, error } = useFileUpload();
  const isUploading = status === "requesting" || status === "uploading" || status === "confirming";

  const [actualObservation, setActualObservation] = useState<Partial<IIncomeEntryArticleBundleObservation> | undefined>(observation);
  const hasObservation = Boolean(observation);
  const hasPhoto = Boolean(actualObservation?.photo_file_id);
  const photoError = localError || error;
  const canSave = canSaveObservation(actualObservation);

  useEffect(() => {
    uploadAttemptRef.current = null;
    reset();
    setLocalError(null);
    setPhotoName(null);
    setConfirmRemove(false);
    if (open) setActualObservation(observation ? { ...observation } : undefined);

    return () => { uploadAttemptRef.current = null; };
  }, [open, bundleId, observation, reset]);

  const handleClose = () => {
    uploadAttemptRef.current = null;
    reset();
    setConfirmRemove(false);
    setLocalError(null);
    onClose();
  };

  const handleChangeObservation = (field: keyof IIncomeEntryArticleBundleObservation, value: any) => {
    setActualObservation((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    if (!open || isUploading || uploadAttemptRef.current || !bundleId || !actualObservation?.reason) return;
    onSave(bundleId, {
      ...actualObservation,
      note: actualObservation.note?.trim() || null,
      photo: actualObservation.photo || null,
      reason: actualObservation.reason,
      income_entry_article_bundle_id: bundleId,
    });
    handleClose();
  };

  const handleRemove = () => {
    if (!open || isUploading || uploadAttemptRef.current || !bundleId) return;
    setConfirmRemove(false);
    onRemove();
    handleClose();
  };

  const handleUploadPhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file || !open || isUploading || uploadAttemptRef.current) return;
    setLocalError(null);
    if (!FILE_IMAGE_CONTENT_TYPES.some((type) => type === file.type)) {
      setLocalError("Solo se permiten fotos JPEG o PNG. HEIC y otros formatos no estan soportados.");
      return;
    }

    const attempt = Symbol();
    uploadAttemptRef.current = attempt;
    try {
      const confirmed = await upload(file);
      if (confirmed && uploadAttemptRef.current === attempt) {
        setActualObservation((current) => ({ ...current, photo_file_id: confirmed.fileId, photo: null }));
        setPhotoName(confirmed.originalName);
      }
    } finally {
      if (uploadAttemptRef.current === attempt) uploadAttemptRef.current = null;
    }
  };

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 18,
            borderTopRightRadius: 18,
            px: 2.25,
            pt: 2.25,
            pb: 2.75,
            maxHeight: "85%",
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {hasObservation ? "Editar observación" : "Cargar observación"}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Fardo{" "}
              <Box
                component="span"
                sx={{ ...typographyStyles.monospace, color: "warning.main" }}
              >
                {bundleId}
              </Box>
            </Typography>
          </Box>
          <IconButton onClick={handleClose} aria-label="Cerrar" edge="end">
            <MdClose />
          </IconButton>
        </Stack>

        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "block", mt: 2.25, mb: 1 }}
        >
          Motivo
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {OBSERVATION_REASON_OPTIONS.map((reason) => {
            const label = OBSERVATION_REASON_MAP[reason];
            const active = actualObservation?.reason === reason;
            return (
              <Chip
                key={reason}
                label={label}
                onClick={() => handleChangeObservation("reason", reason)}
                color={active ? "warning" : "default"}
                variant={active ? "filled" : "outlined"}
                aria-pressed={active}
              />
            );
          })}
        </Box>

        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "block", mt: 2.25, mb: 1 }}
        >
          Nota del operario (opcional)
        </Typography>
        <TextField
          value={actualObservation?.note || ""}
          onChange={(e) => handleChangeObservation("note", e.target.value)}
          placeholder="Describí lo que encontraste…"
          multiline
          minRows={3}
          fullWidth
        />

        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "block", mt: 2.25, mb: 1 }}
        >
          Evidencia (opcional)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          JPEG o PNG, hasta 10 MiB.
        </Typography>
        <Button
          component="label"
          variant="outlined"
          fullWidth
          color={hasPhoto ? "success" : "inherit"}
          startIcon={hasPhoto ? <MdCheckCircle /> : <MdPhotoCamera />}
          disabled={isUploading}
          sx={{
            justifyContent: "flex-start",
            borderStyle: "dashed",
            py: 1.5,
          }}
        >
          {hasPhoto ? "Cambiar foto" : "Agregar foto"}
          <input
            type="file"
            accept={FILE_IMAGE_CONTENT_TYPES.join(",")}
            aria-label="Seleccionar foto de la observacion"
            capture="environment"
            hidden
            disabled={isUploading}
            onChange={handleUploadPhoto}
          />
        </Button>
        {hasPhoto && (
          <Typography variant="caption" sx={{ mt: 1, overflowWrap: "anywhere" }}>
            {photoName || "Foto previamente guardada"}
          </Typography>
        )}
        {isUploading && (
          <Box role="status" aria-live="polite" sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {status === "requesting" ? "Solicitando permiso de carga..." :
                status === "confirming" ? "Verificando foto..." : `Subiendo foto: ${progress}%`}
            </Typography>
            <LinearProgress
              aria-label="Progreso de carga de foto"
              variant={status === "uploading" ? "determinate" : "indeterminate"}
              value={progress}
            />
          </Box>
        )}
        {photoError && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {photoError}{hasPhoto && " Se conserva la foto anterior."}
          </Alert>
        )}

        {hasObservation && (
          <Button
            variant="text"
            color="error"
            fullWidth
            startIcon={<MdDeleteOutline />}
            onClick={() => setConfirmRemove(true)}
            disabled={isUploading}
            sx={{ mt: 1.5 }}
          >
            Quitar observación
          </Button>
        )}

        <Stack
          direction="row"
          spacing={1.25}
          sx={{ mt: hasObservation ? 1.5 : 2.75 }}
        >
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            onClick={handleClose}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!canSave || isUploading}
            onClick={handleSave}
            sx={{ px: 2 }}
          >
            Guardar observación
          </Button>
        </Stack>
      </Drawer>

      <ConfirmDialog
        open={confirmRemove}
        titulo="Quitar observación"
        description={
          <>
            Se van a eliminar el motivo, la nota y la foto de{" "}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              Fardo {bundleId}
            </Box>
            . Volverá a quedar pendiente de verificar.
          </>
        }
        confirmLabel="Sí, quitar observación"
        cancelLabel="Cancelar"
        onConfirm={handleRemove}
        onCancel={() => setConfirmRemove(false)}
      />
    </>
  );
}
